import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { ConnectionStatus, TypedSocket } from "@/socket/socketTypes";
import { createClientSocket } from "@/socket/socketClient";
import { reportError } from "@/lib/errorReporter";

interface SocketStore {
  socket: TypedSocket;
  status: ConnectionStatus;
  connect: () => void;
  disconnect: () => void;
}

// AppError messages thrown by sinek-api's socketAuth middleware. These are auth
// denials, not client bugs: routine for logged-out/expired visitors, and the
// server is the source of truth for whether any of them is worth tracking (it
// trackErrors the genuinely suspicious cases — invalid session / deleted user —
// with full user context the client does not have). Reporting them here only
// produces noise (missing/invalid token) or duplicates (invalid session /
// user not found) what the server already records.
const SERVER_AUTH_DENIAL_MESSAGES = new Set([
  "Missing authentication token",
  "Invalid authentication token",
  "Invalid session",
  "User not found",
]);

const withImmer = immer<SocketStore>((set) => {
  const clientSocket = createClientSocket();

  clientSocket.on("connect", () => {
    set({ status: ConnectionStatus.Connected });
  });

  clientSocket.on("disconnect", (reason) => {
    set({ status: ConnectionStatus.Disconnected });
    if (reason === "io server disconnect") {
      clientSocket.disconnect();
    }
  });

  clientSocket.on("connect_error", (err) => {
    console.error("[socket] connect error:", err.message);
    // active === true -> transient network failure that auto-retries, noise.
    if (clientSocket.active) return;
    // active === false -> the server denied the connection or a fatal error.
    // Auth denials are classified server-side, not here (see the set above) —
    // report only genuinely unexpected fatal connect errors.
    if (SERVER_AUTH_DENIAL_MESSAGES.has(err.message)) return;
    reportError({ name: "SocketConnectError", message: err.message });
  });

  // Terminal reconnection failure: the client exhausted its reconnection
  // attempts and gave up. Per-attempt events are intentionally NOT reported
  // (noise) — only this terminal state and a fatal manager-level error. Both
  // are Manager (clientSocket.io) events, not Socket events.
  clientSocket.io.on("reconnect_failed", () => {
    reportError({
      name: "SocketReconnectFailed",
      message: "Socket reconnection failed after all attempts",
    });
  });

  clientSocket.io.on("error", (err) => {
    const message = err instanceof Error ? err.message : String(err);
    reportError({ name: "SocketError", message });
  });

  return {
    socket: clientSocket,
    status: ConnectionStatus.Disconnected,

    connect() {
      clientSocket.connect();
    },

    disconnect() {
      clientSocket.disconnect();
    },
  };
});

export const useSocketStore = create(withImmer);
